/**
 * Logger Utility
 * 
 * Provides standardized logging functionality for the application
 * Allows for different log levels and context-based logging
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  context?: string;
  data?: Record<string, any>;
}

/**
 * Safe JSON stringification that handles circular references
 */
function safeStringify(obj: any): string {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
    }
    return value;
  }, 2);
}

/**
 * Get the current environment
 */
function getEnvironment(): 'development' | 'production' | 'test' {
  return (process.env.NODE_ENV as 'development' | 'production' | 'test') || 'development';
}

/**
 * Determines if a log level should be displayed based on environment
 */
function shouldLog(level: LogLevel): boolean {
  const env = getEnvironment();
  
  // Always log errors
  if (level === 'error') return true;
  
  // In production, only show errors and warnings
  if (env === 'production') {
    return level === 'warn';
  }
  
  // In development and test, show all logs
  return true;
}

/**
 * Logger class implementation
 */
class LoggerImplementation {
  /**
   * Debug level logging
   */
  debug(message: string, context?: LogContext): void {
    if (shouldLog('debug')) {
      console.debug(
        `[DEBUG] ${message}`,
        context ? safeStringify(context) : ''
      );
    }
  }
  
  /**
   * Info level logging
   */
  info(message: string, context?: LogContext): void {
    if (shouldLog('info')) {
      console.info(
        `[INFO] ${message}`,
        context ? safeStringify(context) : ''
      );
    }
  }
  
  /**
   * Warning level logging
   */
  warn(message: string, context?: LogContext): void {
    if (shouldLog('warn')) {
      console.warn(
        `[WARN] ${message}`,
        context ? safeStringify(context) : ''
      );
    }
  }
  
  /**
   * Error level logging
   */
  error(message: string, context?: LogContext): void {
    if (shouldLog('error')) {
      console.error(
        `[ERROR] ${message}`,
        context ? safeStringify(context) : ''
      );
    }
  }
}

// Create a singleton instance
export const Logger = new LoggerImplementation();

// Default export for convenience
export default Logger;
